/**
 * Caixa de exibição da mídia na bolha (estilo WhatsApp Web): retrato 190px,
 * paisagem 300px de largura; altura máx 430px (object-cover cropa o excesso).
 * Sem dimensões → fallback 300px de largura (altura cresce com o conteúdo).
 *
 * Fonte ÚNICA de verdade da largura da mídia, usada por:
 *  - MediaContent (dimensiona a imagem)
 *  - MessageBubble (prende a largura da bolha à da mídia, pra a legenda/corpo
 *    não esticar além da imagem — senão o texto vai até max-w-480 e desalinha).
 */
export function mediaBoxSize(
  width?: number | null,
  height?: number | null,
): { width: number; height: number | null } {
  if (!width || !height || width <= 0 || height <= 0) {
    return { width: 300, height: null };
  }
  const isPortrait = height > width;
  const maxW = isPortrait ? 190 : 300;
  const maxH = 430;
  // Largura no maxW (a menos que a imagem seja menor — aí mantém natural).
  const scale = width > maxW ? maxW / width : 1;
  const fitW = Math.round(width * scale);
  const fitH = Math.round(height * scale);
  return { width: fitW, height: Math.min(fitH, maxH) };
}

/**
 * Caixa do VÍDEO. Mora aqui ao lado da da imagem de propósito.
 *
 * O vídeo tem medidas próprias (retrato até 240 de largura, altura máx 320 —
 * mais contido que a imagem, porque a bolha já carrega o botão de play) e
 * precisa de altura SEMPRE definida: a caixa é desenhada antes do arquivo
 * carregar e dar play não pode mudar o tamanho da bolha.
 *
 * ⚠️ Estava solta dentro do MediaContent, e foi exatamente isso que quebrou:
 * a bolha se media por `mediaBoxSize` (retrato 190) enquanto o vídeo se
 * desenhava por esta (retrato 240), e a prévia vazava 26px pra FORA da bolha
 * em vídeo retrato pouco alto — proporção entre 0,75 e 1, o caso da gravação
 * de tela de uma janela. Vídeo de celular em pé escondia o defeito porque a
 * altura limitava antes da largura. Quem dimensiona a bolha agora chama esta
 * mesma função; o teste de media-box amarra as duas.
 */
export function videoBoxSize(
  width?: number | null,
  height?: number | null,
): { width: number; height: number } {
  if (!width || !height || width <= 0 || height <= 0) return { width: 300, height: 169 };
  const isPortrait = height > width;
  const maxW = isPortrait ? 240 : 300;
  const maxH = 320;
  const scale = Math.min(maxW / width, maxH / height, 1);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Largura da caixa que o conteúdo vai ocupar, por tipo de mídia. É o que a
 *  bolha precisa saber pra não ficar menor que o que carrega dentro. */
export function larguraDaMidia(
  tipo: 'image' | 'video' | string,
  width?: number | null,
  height?: number | null,
): number {
  return tipo === 'video' ? videoBoxSize(width, height).width : mediaBoxSize(width, height).width;
}
